import { ApolloQueryResult, useApolloClient } from "@apollo/client";
import ChevronRight from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, FormControl, InputAdornment, List, styled, Switch, TextField, Typography, useTheme } from "@mui/material";
import { Loading } from "shared-ui/global/Loading";
import { useDebounce } from "shared-ui/hooks/useDebounce";
import { useLocaleContext } from "shared-ui/contexts/Locale/LocaleContext";
import { useTranslation } from "react-i18next";
import { useNutritionFlags } from "../../../hooks/useNutritionFlags";
import { sortByFn } from "@notemeal/utils-sort";
import {
  AthleteAnthropometryFilter,
  AthleteForMealPlanTemplateAssignmentFragment,
  AthletesForMealPlanTemplateAssignmentDocument,
  AthletesForMealPlanTemplateAssignmentQuery,
  AthletesForMealPlanTemplateAssignmentQueryVariables,
  GoalTypeFragment,
  NamedTagForSelectionFragment,
  TeamPreviewFragment,
  useAthletesForMealPlanTemplateAssignmentQuery,
  useSelectedAthletesForCustomTagCreateQuery,
} from "apps/web/src/types";
import NamedTagSelector from "apps/web/src/views/Tags/Selectors/NamedTagSelector";
import TeamSelector from "apps/web/src/views/Tags/Selectors/TeamSelector";
import { useCallback, useMemo, useState } from "react";
import WeightTargetTypeSelect from "../../Goal/TypeSelect";
import useInfiniteCursorConnectionScroll from "../../universal/InfiniteScroll/useInfiniteCursorConnectionScroll";
import LabeledSelect from "../../universal/LabeledSelect";
import { BulkAssignMealPlanTemplateAthleteInfiniteScroll } from "./BulkAssignMealPlanTemplateAthleteInfiniteScroll";
import { BulkAssignMealPlanTemplateAthleteListItem } from "./BulkAssignMealPlanTemplateAthleteListItem";
import { useBulkAssignMealPlanTemplate } from "./BulkAssignMealPlanTemplateContext";
import { metricAnthropometryEntryFormDefaultValues } from "../../AnthropometryEntry/Form/MetricAnthropometryEntryFormSchema";
import { anthropometryEntryFormDefaultValues } from "../../AnthropometryEntry/Form/AnthropometryEntryFormSchema";
import { BulkAssignMealPlanTemplateAthleteEditModal } from "./BulkAssignMealPlanTemplateAthleteEditModal";
import { TWTooltip } from "apps/web/src/componentLibrary/TWTooltip/TWTooltip";
import { createStyles, makeStyles } from "@mui/styles";

const ListHeader = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "16px 16px 0px 16px",
}));

const AthleteContainer = styled("div")(
  ({
    theme: {
      spacing,
      palette: { grey },
    },
  }) => ({
    flex: 1,
    height: "calc(100vh - 400px)",
    minHeight: "450px",
    border: `1px solid ${grey[500]}`,
    borderRadius: "5px",
    marginBottom: spacing(2),
  })
);

const athleteListStyle = { overflowY: "auto", height: "calc(100% - 40px)" };
const listItemStyle = { width: `calc(100% - ${1})` };

const useStyles = makeStyles(() => {
  return createStyles({
    filterController: {
      marginBottom: 0,
    },
  });
});

interface BulkAssignMealPlanTemplateAthleteSelectProps {
  athletes: AthleteForMealPlanTemplateAssignmentFragment[];
  setAthletes: (athletes: AthleteForMealPlanTemplateAssignmentFragment[]) => void;
}

type AnthropometryFilterOption = { id: AthleteAnthropometryFilter | "view_all"; name: string };

const ANTHROPOMETRY_FILTER_OPTIONS: AnthropometryFilterOption[] = [
  { id: "has_anthropometry_entry", name: "Has anthro entry" },
  { id: "no_anthropometry_entry", name: "No anthro entry" },
  { id: "view_all", name: "View all" },
];

export const BulkAssignMealPlanTemplateAthleteSelect = ({ athletes, setAthletes }: BulkAssignMealPlanTemplateAthleteSelectProps) => {
  const { t } = useTranslation();
  const {
    palette: { grey },
  } = useTheme();
  const classes = useStyles();
  const { isTeamScopedPermissionsEnabled } = useNutritionFlags();
  const { mealPlanTemplate } = useBulkAssignMealPlanTemplate();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 200);
  const [teamsFilter, setTeamsFilter] = useState<TeamPreviewFragment[]>([]);
  const [tagsFilter, setTagsFilter] = useState<NamedTagForSelectionFragment[]>([]);
  const [weightTargetTypeFilter, setWeightTargetTypeFilter] = useState<GoalTypeFragment | null>(null);
  const [anthropometryFilter, setAnthropometryFilter] = useState<AnthropometryFilterOption | null>(null);
  const apolloClient = useApolloClient();
  const [allAthletesSelected, setAllAthletesSelected] = useState(0); // Very quirky b/c of filters

  const { isMetricLocale } = useLocaleContext();
  const defaultFormValues = isMetricLocale ? metricAnthropometryEntryFormDefaultValues : anthropometryEntryFormDefaultValues;
  const [initialFormState, setInitialFormState] = useState(defaultFormValues());
  const [anthropometryModalOpen, setAnthropometryModalOpen] = useState(false);
  const [athleteForAnthropometryEntry, setAthleteForAnthropometryEntry] = useState<AthleteForMealPlanTemplateAssignmentFragment>();
  const [showOnlyAthletesWithNoMealPlans, setShowOnlyAthletesWithNoMealPlans] = useState(false);

  const handleAnthropometryModalClose = () => {
    setInitialFormState(defaultFormValues());
    setAnthropometryModalOpen(false);
    setAthleteForAnthropometryEntry(undefined);
  };

  const hasFilter =
    searchQuery !== "" ||
    teamsFilter.length > 0 ||
    tagsFilter.length > 0 ||
    weightTargetTypeFilter !== null ||
    anthropometryFilter !== null ||
    showOnlyAthletesWithNoMealPlans;
  const resetFilter = () => {
    setSearchQuery("");
    setTeamsFilter([]);
    setTagsFilter([]);
    setWeightTargetTypeFilter(null);
    setAnthropometryFilter(null);
    setShowOnlyAthletesWithNoMealPlans(false);
    resetSelectAllButtonState();
  };

  const rmrMethod =
    mealPlanTemplate.__typename === "MacroMealPlanTemplate" ? mealPlanTemplate.macroProtocol.calorieBudget?.rmrMethod ?? null : null;

  const tagFilterIds = useMemo(() => {
    return tagsFilter.map(namedTag => namedTag.tag.id);
  }, [tagsFilter]);

  const teamFilterIds = useMemo(() => {
    return teamsFilter.map(({ id }) => id);
  }, [teamsFilter]);

  const selectedAthleteIds = useMemo(() => {
    return athletes.map(({ id }) => id);
  }, [athletes]);

  const { data, loading } = useSelectedAthletesForCustomTagCreateQuery({
    variables: {
      query: debouncedQuery,
      tagIds: tagFilterIds,
      teamIds: teamFilterIds,
    },
  });

  const searchResults = useInfiniteCursorConnectionScroll({
    useCursorConnectionQuery: useAthletesForMealPlanTemplateAssignmentQuery,
    getQueryVariablesFromPagination: useCallback(
      ({ cursor, limit }) => ({
        variables: {
          pagination: { cursor, limit },
          query: debouncedQuery,
          tagIds: tagFilterIds,
          teamIds: teamFilterIds,
          weightTargetTypeId: weightTargetTypeFilter?.id ?? null,
          anthropometryFilter: anthropometryFilter?.id === "view_all" ? null : anthropometryFilter?.id ?? null,
          rmrMethod,
          hasNoMealPlans: showOnlyAthletesWithNoMealPlans,
        },
      }),
      [debouncedQuery, tagFilterIds, teamFilterIds, weightTargetTypeFilter, anthropometryFilter, rmrMethod, showOnlyAthletesWithNoMealPlans]
    ),
    queryKey: "athletesForMealPlanTemplateAssignmentCursorConnection",
    edgesAreEqual: useCallback(
      (athlete1: AthleteForMealPlanTemplateAssignmentFragment, athlete2: AthleteForMealPlanTemplateAssignmentFragment) => {
        return athlete1.id === athlete2.id;
      },
      []
    ),
    limit: 25,
  });

  const onClickItem = (athlete: AthleteForMealPlanTemplateAssignmentFragment) => {
    const included = athletes.some(({ id }) => id === athlete.id);
    if (!included) {
      setAthletes(
        sortByFn([...athletes, athlete], ath => `${ath.lastName}, ${ath.firstName}`) as AthleteForMealPlanTemplateAssignmentFragment[]
      );
    }
  };

  const onClickNoAnthropometryItem = (athlete: AthleteForMealPlanTemplateAssignmentFragment) => {
    setAnthropometryModalOpen(true);
    setAthleteForAnthropometryEntry(athlete);
  };

  const onSelectAll = async () => {
    const limit = 100;

    let athletesToAdd: AthleteForMealPlanTemplateAssignmentFragment[] = [];
    let hasError = false;
    let hasNextPage = true;
    let cursor = null;

    while (!hasError && hasNextPage) {
      const result = (await apolloClient.query<
        AthletesForMealPlanTemplateAssignmentQuery,
        AthletesForMealPlanTemplateAssignmentQueryVariables
      >({
        query: AthletesForMealPlanTemplateAssignmentDocument,
        variables: {
          pagination: { limit, cursor },
          query: debouncedQuery,
          tagIds: tagFilterIds,
          teamIds: teamFilterIds,
          weightTargetTypeId: weightTargetTypeFilter?.id ?? null,
          anthropometryFilter: anthropometryFilter?.id === "view_all" ? null : anthropometryFilter?.id ?? null,
          rmrMethod,
          hasNoMealPlans: showOnlyAthletesWithNoMealPlans,
        },
      })) as ApolloQueryResult<AthletesForMealPlanTemplateAssignmentQuery>; // casting because theres some sort of ts bug with the query in a while loop

      hasNextPage = result.data.athletesForMealPlanTemplateAssignmentCursorConnection.pageInfo.hasNextPage;
      cursor = result.data.athletesForMealPlanTemplateAssignmentCursorConnection.pageInfo.endCursor;
      hasError = result.errors !== undefined;

      athletesToAdd = [
        ...athletesToAdd,
        ...result.data.athletesForMealPlanTemplateAssignmentCursorConnection.edges
          .filter(a => !!a.mostRecentAnthropometryEntryForRmrMethodOrFailureReason.anthropometryEntry)
          .filter(a => !selectedAthleteIds.includes(a.id)),
      ];
    }
    setAthletes(
      sortByFn(
        [...athletes, ...athletesToAdd],
        ath => `${ath.lastName}, ${ath.firstName}`
      ) as AthleteForMealPlanTemplateAssignmentFragment[]
    );
    setAllAthletesSelected([...athletes, ...athletesToAdd].length);
  };

  const isSelectAllDisabled = allAthletesSelected > 0 && allAthletesSelected === athletes.length;

  const resetSelectAllButtonState = () => {
    setAllAthletesSelected(0);
  };

  const onClearAll = () => {
    setAthletes([]);
    setAllAthletesSelected(0);
  };

  const filteredRHSAthletes = useMemo(() => {
    if (!hasFilter) {
      return sortByFn(athletes, ath => `${ath.lastName}, ${ath.firstName}`);
    }
    if (!data || loading) {
      return undefined;
    }
    const possibleAthleteIds = data.athletesForCustomTagCreation.map(({ id }) => id);
    return athletes.filter(({ id }) => possibleAthleteIds.includes(id));
  }, [data, loading, hasFilter, athletes]);

  const onSelectedAthleteClick = (athlete: AthleteForMealPlanTemplateAssignmentFragment) => {
    setAthletes(athletes.filter(({ id }) => id !== athlete.id));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
        <TextField
          sx={{ mt: 0, width: 456 }}
          placeholder={t("mealPlanTemplate.bulkAssign.athleteSelect.searchPlaceholder")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <TWTooltip title={t("mealPlanTemplate.bulkAssign.athleteSelect.showOnlyNewTooltip")}>
          <FormControl>
            <Switch
              size="medium"
              inputProps={{
                "aria-label": t("mealPlanTemplate.bulkAssign.athleteSelect.showOnlyNewTooltip"),
              }}
              checked={showOnlyAthletesWithNoMealPlans}
              onClick={e => e.stopPropagation()}
              onChange={e => setShowOnlyAthletesWithNoMealPlans(e.target.checked)}
            />
          </FormControl>
        </TWTooltip>
        <Typography variant="body2Medium" sx={{ color: grey[500] }}>{t("mealPlanTemplate.bulkAssign.athleteSelect.showOnlyNewLabel")}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "flex-end", my: 2, mx: 0, gap: 2 }}>
        <TeamSelector
          sx={{ mt: 0, width: 220 }}
          selectedTeams={teamsFilter}
          onChange={teams => {
            setTeamsFilter(teams);
            resetSelectAllButtonState();
          }}
          inputPlaceholder="Teams"
          limitTags={2}
        />
        {!isTeamScopedPermissionsEnabled && (
          <NamedTagSelector
            sx={{ mt: 0, width: 220 }}
            selectedNamedTags={tagsFilter}
            onChange={tags => {
              setTagsFilter(tags);
              resetSelectAllButtonState();
            }}
            limitTags={2}
          />
        )}
        <Box sx={{ mt: 0, width: 220 }}>
          <WeightTargetTypeSelect
            onChange={wtt => {
              setWeightTargetTypeFilter(wtt);
              resetSelectAllButtonState();
            }}
            selectedType={weightTargetTypeFilter}
            className={classes.filterController}
          />
        </Box>
        <Box sx={{ mt: 0, width: 220 }}>
          <LabeledSelect
            placeholder="Anthropometry"
            options={ANTHROPOMETRY_FILTER_OPTIONS}
            selectedOption={anthropometryFilter}
            onChange={option => {
              setAnthropometryFilter(option);
              resetSelectAllButtonState();
            }}
            optionToName={o => o.name}
            textFieldProps={{
              margin: "dense",
            }}
            className={classes.filterController}
          />
        </Box>
        {hasFilter && (
          <Button variant="textInformational" onClick={() => resetFilter()}>
            Clear Filters
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <AthleteContainer>
          <ListHeader>
            <Typography variant="h4">{t("mealPlanTemplate.bulkAssign.athleteSelect.availableAthletes")}</Typography>
            <Button onClick={onSelectAll} size="small" disabled={isSelectAllDisabled} variant="textInformational">
              Select All
            </Button>
          </ListHeader>
          <BulkAssignMealPlanTemplateAthleteInfiniteScroll
            searchResults={searchResults}
            onClickItem={onClickItem}
            onClickNoAnthropometryItem={onClickNoAnthropometryItem}
            selectedAthleteIds={selectedAthleteIds}
            sx={athleteListStyle}
          />
        </AthleteContainer>
        <ChevronRight fontSize="large" />
        <AthleteContainer>
          <ListHeader sx={{ gap: 1 }}>
            <Typography variant="h4">{t("mealPlanTemplate.bulkAssign.athleteSelect.selectedAthletes", { count: athletes.length })}</Typography>
            {athletes.length ? (
              <>
                <Button onClick={onClearAll} size="small" variant="textInformational">
                  Clear All
                </Button>
                <Box sx={{ flex: 1 }}></Box>
              </>
            ) : null}
            <Typography variant="body1Medium" sx={{ color: grey[500] }}>
              ({filteredRHSAthletes ? filteredRHSAthletes.length : 0} Results Shown)
            </Typography>
          </ListHeader>
          <List disablePadding sx={athleteListStyle}>
            {filteredRHSAthletes === undefined ? (
              <Loading />
            ) : (
              filteredRHSAthletes.map(athlete => {
                return (
                  <BulkAssignMealPlanTemplateAthleteListItem
                    sx={listItemStyle}
                    athlete={athlete}
                    key={athlete.id}
                    onClick={onSelectedAthleteClick}
                    showRemove
                  />
                );
              })
            )}
          </List>
        </AthleteContainer>
        {anthropometryModalOpen && athleteForAnthropometryEntry && (
          <BulkAssignMealPlanTemplateAthleteEditModal
            athleteId={athleteForAnthropometryEntry.id}
            open={anthropometryModalOpen}
            onClose={handleAnthropometryModalClose}
            initialFormState={initialFormState}
            athleteSex={athleteForAnthropometryEntry.sex}
            requireAge={athleteForAnthropometryEntry.mostRecentAnthropometryEntryForRmrMethodOrFailureReason.failureReason?.includes("ERROR_AGE_REQUIRED") ? true : false}
            rmrMethod={rmrMethod}
          />
        )}
      </Box>
    </Box>
  );
};
